/**
 * PomodoroStats - Daily statistics display for the Pomodoro module.
 *
 * Shows: total completed sessions, total focus minutes, and a per-task
 * breakdown (task name + session count). Uses loadDailyStats() on mount
 * to populate data from the Pomodoro store. Includes a ProgressRing for
 * visual summary of the day's focus progress.
 *
 * @see Requirements 7.6
 */

import { useEffect } from 'react';
import { usePomodoroStore } from '@/stores';
import { useTaskStore } from '@/stores';
import { ProgressRing } from '@/components/ui/ProgressRing';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format minutes into a human-readable string (e.g. "1h 25m" or "45m"). */
function formatFocusTime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PomodoroStats() {
  const { dailyStats, config, loadDailyStats } = usePomodoroStore();
  const { tasks } = useTaskStore();

  // Load daily stats on mount
  useEffect(() => {
    void loadDailyStats();
  }, [loadDailyStats]);

  // Calculate progress: how many sessions completed vs sessionsBeforeLongBreak target
  const totalSessions = dailyStats?.totalSessions ?? 0;
  const totalFocusMinutes = dailyStats?.totalFocusMinutes ?? 0;
  const sessionsByTask = dailyStats?.sessionsByTask ?? {};

  // Daily goal: 1 full cycle (sessionsBeforeLongBreak)
  const dailyGoal = config.sessionsBeforeLongBreak;
  const progressPercent = Math.min(100, Math.round((totalSessions / dailyGoal) * 100));

  // Resolve task titles from IDs
  const taskBreakdown = Object.entries(sessionsByTask).map(([taskId, count]) => {
    const task = tasks.find((t) => t.taskId === taskId);
    return {
      taskId,
      taskTitle: task?.title ?? 'Unknown Task',
      sessionCount: count,
    };
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <h3 className="text-base font-semibold text-text-primary">Today's Focus</h3>

      {/* Progress ring + summary */}
      <div className="flex items-center gap-5">
        <ProgressRing
          progress={progressPercent}
          size={72}
          strokeWidth={7}
          color="var(--color-primary)"
          label={`${totalSessions}`}
        />

        <div className="flex flex-col gap-1">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{totalSessions}</span>
            {' '}session{totalSessions !== 1 ? 's' : ''} completed
          </p>
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{formatFocusTime(totalFocusMinutes)}</span>
            {' '}focus time
          </p>
        </div>
      </div>

      {/* Per-task breakdown */}
      {taskBreakdown.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-text-secondary">Per Task</h4>
          <ul className="space-y-1.5" aria-label="Focus sessions per task">
            {taskBreakdown.map(({ taskId, taskTitle, sessionCount }) => (
              <li
                key={taskId}
                className="flex items-center justify-between px-3 py-2 bg-bg-secondary rounded-lg"
              >
                <span className="text-sm text-text-primary truncate max-w-[200px]">
                  {taskTitle}
                </span>
                <span className="text-xs font-medium text-text-secondary bg-bg-card px-2 py-0.5 rounded-full">
                  {sessionCount} session{sessionCount !== 1 ? 's' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Empty state */}
      {totalSessions === 0 && (
        <p className="text-sm text-text-secondary text-center py-4">
          No focus sessions yet today. Start one to track your progress!
        </p>
      )}
    </div>
  );
}

PomodoroStats.displayName = 'PomodoroStats';
